import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { User, Phone, Mail, Lock, ArrowLeft, Home } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortalAuthPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  
  const [loginData, setLoginData] = useState({ phone: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', phone: '', email: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/portal/login`, loginData);
      localStorage.setItem('portal_token', response.data.token);
      localStorage.setItem('portal_user', JSON.stringify(response.data.user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      toast.success('Login successful!');
      navigate('/portal/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/portal/register`, registerData);
      localStorage.setItem('portal_token', response.data.token);
      localStorage.setItem('portal_user', JSON.stringify(response.data.user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      toast.success('Registration successful!');
      navigate('/portal/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-brand-navy relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy-light to-brand-navy opacity-90" />
        <div className="relative z-10 flex flex-col justify-center items-center w-full px-12">
          <div className="mb-8">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-2xl">
              <span className="text-4xl font-bold text-brand-navy font-heading">A</span>
            </div>
          </div>
          <h1 className="text-4xl font-bold text-white text-center mb-4 font-heading">
            Customer Portal
          </h1>
          <p className="text-brand-gold text-lg text-center mb-8">
            अथर्व फायनान्स सोल्युशन
          </p>
          <div className="text-slate-300 text-center max-w-md">
            <p className="text-sm mb-4">Track Your Loan Status</p>
            <ul className="text-xs text-slate-400 space-y-2">
              <li>✓ Check loan application status</li>
              <li>✓ Upload documents online</li>
              <li>✓ View EMI schedule</li>
              <li>✓ 24/7 access to your account</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="w-full lg:w-1/2 flex flex-col bg-slate-50">
        {/* Header */}
        <div className="p-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-brand-navy transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back to Home</span>
          </Link>
          <Link to="/login" className="text-sm text-brand-navy font-medium hover:underline">
            Staff Login
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center p-8">
          <Card className="w-full max-w-md shadow-xl border-0">
            <CardHeader className="space-y-2 pb-4">
              <div className="lg:hidden flex justify-center mb-4">
                <div className="w-16 h-16 bg-brand-navy rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-brand-gold font-heading">A</span>
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-center font-heading text-brand-navy">
                Customer Portal
              </CardTitle>
              <CardDescription className="text-center text-slate-500">
                Login or register to track your loan
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="portal-login-tab">Login</TabsTrigger>
                  <TabsTrigger value="register" data-testid="portal-register-tab">Register</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-phone">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="login-phone"
                          data-testid="portal-login-phone"
                          type="tel"
                          placeholder="Enter your phone number"
                          value={loginData.phone}
                          onChange={(e) => setLoginData(prev => ({ ...prev, phone: e.target.value }))}
                          className="pl-10 h-12"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="login-password"
                          data-testid="portal-login-password"
                          type="password"
                          placeholder="Enter your password"
                          value={loginData.password}
                          onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                          className="pl-10 h-12"
                          required
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      data-testid="portal-login-submit"
                      className="w-full h-12 bg-brand-navy text-white hover:bg-brand-navy/90 font-semibold"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Logging in...' : 'Login'}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-name"
                          data-testid="portal-register-name"
                          type="text"
                          placeholder="Enter your full name"
                          value={registerData.name}
                          onChange={(e) => setRegisterData(prev => ({ ...prev, name: e.target.value }))}
                          className="pl-10 h-12"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-phone">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-phone"
                          data-testid="portal-register-phone"
                          type="tel"
                          placeholder="Enter your phone number"
                          value={registerData.phone}
                          onChange={(e) => setRegisterData(prev => ({ ...prev, phone: e.target.value }))}
                          className="pl-10 h-12"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-email"
                          data-testid="portal-register-email"
                          type="email"
                          placeholder="Enter your email"
                          value={registerData.email}
                          onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                          className="pl-10 h-12"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-password"
                          data-testid="portal-register-password"
                          type="password"
                          placeholder="Create a password"
                          value={registerData.password}
                          onChange={(e) => setRegisterData(prev => ({ ...prev, password: e.target.value }))}
                          className="pl-10 h-12"
                          required
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      data-testid="portal-register-submit"
                      className="w-full h-12 bg-brand-gold text-brand-navy hover:bg-brand-gold/90 font-semibold"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Registering...' : 'Create Account'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PortalAuthPage;
